import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ContactRequest {
  id: string;
  requester_id: string;
  requested_id: string;
  status: 'pending' | 'accepted' | 'declined';
  message: string | null;
  created_at: string;
  updated_at: string;
  responded_at: string | null;
  requester_profile?: {
    firstname: string;
    lastname: string;
    company?: string;
    role?: string;
  };
}

export interface SharedContact {
  id: string;
  owner_id: string;
  shared_with_id: string;
  contact_data: {
    firstname: string;
    lastname: string;
    email: string;
    phone?: string;
    company?: string;
    role?: string;
  };
  created_at: string;
}

// 🔥 PHASE 2: Hook optimisé - ne charge shared_contacts qu'une fois
export const useContactRequests = (skipSharedContacts = false) => {
  const { user } = useAuth();
  const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
  const [sharedContacts, setSharedContacts] = useState<SharedContact[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchContactRequests = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('contact_requests')
        .select('*')
        .or(`requester_id.eq.${user.id},requested_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContactRequests((data || []) as ContactRequest[]);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchSharedContacts = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('shared_contacts')
        .select('*')
        .eq('shared_with_id', user.id);

      if (error) throw error;
      setSharedContacts((data || []) as SharedContact[]);
    } catch (error) {
      console.error('Error fetching shared contacts:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchContactRequests();
    // 🔥 PHASE 2: Ne charger shared_contacts que si nécessaire
    if (!skipSharedContacts) {
      fetchSharedContacts();
    }
  }, [fetchContactRequests, fetchSharedContacts, skipSharedContacts]);

  const sendContactRequest = async (targetUserId: string, message?: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('contact_requests')
        .insert({
          requester_id: user.id,
          requested_id: targetUserId,
          message: message || null
        });

      if (error) throw error;
      await fetchContactRequests();
      return true;
    } catch (error) {
      console.error('Error sending contact request:', error);
      return false;
    }
  };

  const respondToContactRequest = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      const { error } = await supabase
        .from('contact_requests')
        .update({ status, responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;
      
      await fetchContactRequests();
      if (status === 'accepted') {
        await fetchSharedContacts();
      }
      return true;
    } catch (error) {
      console.error('Error responding to contact request:', error);
      return false;
    }
  };

  const pendingRequests = contactRequests.filter(
    r => r.status === 'pending' && r.requested_id === user?.id
  );
  
  const hasSharedContact = useCallback((userId: string) => {
    return sharedContacts.some(c => c.owner_id === userId);
  }, [sharedContacts]);
  
  const hasPendingRequest = useCallback((userId: string) => {
    return contactRequests.some(r => r.requested_id === userId && r.status === 'pending');
  }, [contactRequests]);

  return {
    contactRequests,
    sharedContacts,
    pendingRequests,
    loading,
    sendContactRequest,
    respondToContactRequest,
    hasSharedContact,
    hasPendingRequest,
    refetch: () => {
      fetchContactRequests();
      fetchSharedContacts();
    }
  };
};
